import React, { FC, memo, useCallback, useMemo } from 'react';
import { Options } from 'model/global';
import { normalizeChoices, normalizeStatus } from 'model/menu';
import formatPriceNumber from 'utils/formatPrice';
import {
  SelectAdd,
  Placeholder,
  ItemCard,
  Header,
  Name,
  Choices,
  ActionButton,
  Footer,
  ItemRow,
  BlockLeft,
  BlockRight,
  Number,
  ItemName,
  ItemStatus,
  ItemPrice,
} from './styles';
import { Props } from './types';

const Modifiers: FC<Props> = ({
  push,
  remove,
  modifiers,
  childrenEntities,
  items,
  isProcessing,
}) => {
  const options = useMemo(
    () =>
      Object.keys(modifiers).reduce<Options>((acum, modifierId) => {
        const alreadyAdded = childrenEntities.find(
          (children) => children.id === modifierId,
        );

        if (alreadyAdded) {
          return acum;
        }
        return [
          ...acum,
          {
            label: modifiers[modifierId].name,
            value: modifierId,
          },
        ];
      }, []),
    [modifiers, childrenEntities],
  );

  const handleAddOption = useCallback(
    (modifierToAdd: string) => {
      if (modifierToAdd) {
        push({
          id: modifierToAdd,
          name: modifiers[modifierToAdd].name,
          type: 'MODIFIER_GROUP',
          childrenEntities: [],
          status: 'NEW',
        });
      }
    },
    [push, modifiers],
  );

  const handleRemoveItem = useCallback(
    (indexToRemove) => {
      remove(indexToRemove);
    },
    [remove],
  );

  return (
    <>
      <SelectAdd
        options={options}
        onAdd={handleAddOption}
        disabled={isProcessing}
      />
      {childrenEntities.length === 0 && (
        <Placeholder>You don&apos;t have any modifier yet</Placeholder>
      )}
      {childrenEntities.map((entity, entityIndex) => {
        const modifier = modifiers[entity.id];
        const choices = normalizeChoices(
          modifier.choices,
          modifier.items.length,
        );
        return (
          <ItemCard key={entity.id}>
            <Header>
              <div>
                <Name>{modifier.name}</Name>
                <Choices>
                  {choices.type} <span>· {choices.selection}</span>
                </Choices>
              </div>

              <ActionButton
                variant="delete"
                disabled={isProcessing}
                onClick={() => handleRemoveItem(entityIndex)}
              />
            </Header>
            <Footer>
              {modifier.items.map((itemId, itemIndex) => {
                const item = items[itemId];
                return (
                  <ItemRow key={item.itemId}>
                    <BlockLeft>
                      <Number>{itemIndex + 1}</Number>
                      <ItemName>{item.name.printName}</ItemName>
                    </BlockLeft>
                    <BlockRight>
                      <ItemStatus>{normalizeStatus(item.status)}</ItemStatus>
                      <ItemPrice>{formatPriceNumber(item.price)}</ItemPrice>
                    </BlockRight>
                  </ItemRow>
                );
              })}
            </Footer>
          </ItemCard>
        );
      })}
    </>
  );
};

export default memo(Modifiers);
